import { oacApi, pipelineNotificationApi } from '@/modules/pipelines/api';
import { useCallback, useEffect, useState } from 'react';
import { EntityNode } from '@/modules/pipelines/model/pipeline-alerts/entity-node';
import { Page } from '@/shared/pagination/export';
import { Loading } from '@/shared/components/export';
import { FormSelect, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faChartLine } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import OACDisplay from './oac-display';
import { RequirePermissions, Permissions } from '@/core/permissions/export';
dayjs.extend(utc);

//TODO - remove me
const allowedPipelines = ['PIPELINE:transwestern'];

const entityNodeSort = (a?: EntityNode, b?: EntityNode) => {
  if (!a) return -1;
  if (!b) return 1;

  const byType = a.type.localeCompare(b.type);

  if (byType !== 0) return byType;

  return a.name.localeCompare(b.name);
};

const TableEntry = ({
  node,
  key,
  onOpen,
  depth,
}: {
  node: EntityNode;
  key: string;
  depth: number;
  onOpen: (open: boolean, node: EntityNode) => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const children = node.relations
    .filter(e => e.type === 'OWNS')
    .filter(value => value.sourceId === node._id);

  const toggleOpen = () => {
    setOpen(!open);
    onOpen(!open, node);
  };

  return (
    <>
      <tr key={node._id} onClick={toggleOpen} className={'cursor-pointer'}>
        <td
          className={`cursor-pointer ps-${depth} ${open && node.oac?.length && 'border-bottom-0'}`}
        >
          {' '}
          <span className={'opacity-0'}>{'—'.repeat(depth)}</span>
          <span
            className={`${!node.oac?.length ? 'me-2' : ''} ${children.length > 0 || node.oac?.length ? '' : 'opacity-0'}`}
          >
            <FontAwesomeIcon
              className={`${node.oac?.length && open ? 'text-warning' : ''}`}
              icon={node.oac?.length ? faChartLine : open ? faCaretDown : faCaretRight}
            />
          </span>{' '}
          {node.name}
        </td>
        <td className={`${open && node.oac?.length && 'border-bottom-0'}`}>{node.type}</td>
      </tr>

      <RequirePermissions allow={Permissions.ALPHA_ACCESS_MARKET_SIGNALS}>
        {open && node.oac && node.oac.length > 0 && (
          <tr>
            <td colSpan={2}>
              <OACDisplay oacData={node.oac} />
            </td>
          </tr>
        )}
      </RequirePermissions>

      {open &&
        children
          .map(relation => relation.value)
          .sort(entityNodeSort)
          .map((node, index) => {
            if (!node)
              return (
                <tr key={index + '-empty'} className={'text-center text-muted small em'}>
                  <td colSpan={2}>...loading...</td>
                </tr>
              );

            return <TableEntry node={node} key={node._id} onOpen={onOpen} depth={depth + 1} />;
          })}
    </>
  );
};

export default function AssetNavigator() {
  const [entities, setEntities] = useState<Page<EntityNode>>();
  const [selectedEntity, setSelectedEntity] = useState<EntityNode>();

  useEffect(() => {
    pipelineNotificationApi.getPipelineEntities(['PIPELINE']).then(setEntities);
  }, []);

  useEffect(() => {
    if (!entities || selectedEntity) return;

    const initialSelection = entities.content.find(value => allowedPipelines.includes(value._id));
    if (initialSelection) setSelectedEntity(initialSelection);
  }, [entities, selectedEntity]);

  const populateChildren = useCallback(async (node: EntityNode) => {
    const relationsToFetch = node.relations.filter(r => r.type === 'OWNS' && !r.value);
    if (relationsToFetch.length === 0) return;

    const now = dayjs(new Date());
    const start = now.subtract(5, 'days').format('YYYY-MM-DD');
    const end = now.format('YYYY-MM-DD');

    const updatedRelations = await Promise.all(
      relationsToFetch.map(async relation => {
        const child = await pipelineNotificationApi.getPipelineEntityById(relation.targetId);

        if (relation.targetId.startsWith('ASSET')) {
          const history = await oacApi.getOACDay(relation.targetId, start, end);
          child.oac = history;
        }

        return { ...relation, value: child };
      }),
    );

    setSelectedEntity(prev => {
      if (!prev) return prev;

      const updateNodeInTree = (current: EntityNode): EntityNode => {
        return {
          ...current,
          relations: current.relations
            .filter(rel => rel.type === 'OWNS')
            .map(rel => {
              if (current._id === node._id) {
                const match = updatedRelations.find(ur => ur.targetId === rel.targetId);
                return match ? match : rel;
              } else return rel.value ? { ...rel, value: updateNodeInTree(rel.value) } : rel;
            }),
        };
      };

      return updateNodeInTree(prev);
    });
  }, []);

  useEffect(() => {
    if (!selectedEntity) return;

    const hasUnloadedChildren = selectedEntity.relations.some(r => r.type === 'OWNS' && !r.value);
    if (hasUnloadedChildren) populateChildren(selectedEntity);
    // eslint-disable-next-line
  }, [selectedEntity?._id]);

  if (!entities) return <Loading />;

  return (
    <>
      <FormSelect>
        {entities.content
          .filter(value => allowedPipelines.includes(value._id))
          .map(entity => (
            <option key={entity._id} selected={entity._id === selectedEntity?._id}>
              {entity.name}
            </option>
          ))}
      </FormSelect>

      <Table className={'mt-3'}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {selectedEntity?.relations
            .filter(e => e.value)
            .filter(e => e.type === 'OWNS')
            .map(e => e.value as EntityNode)
            .sort(entityNodeSort)
            .map(node => {
              return (
                <TableEntry
                  node={node}
                  key={node._id}
                  onOpen={(open, node) => open && populateChildren(node)}
                  depth={0}
                />
              );
            })}
        </tbody>
      </Table>
    </>
  );
}
